import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Eye, EyeOff, Lock, CheckCircle2, ExternalLink, AlertCircle, Copy, Check } from 'lucide-react'

export default function ChangePasswordForm() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const resetToken = searchParams.get('reset_password_token')
    const type = searchParams.get('type') || 'candidate'

    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [status, setStatus] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [csrfToken, setCsrfToken] = useState(null)

    const [validationError, setValidationError] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isWebView, setIsWebView] = useState(false)
    const [isIOS, setIsIOS] = useState(false)
    const [showInstructions, setShowInstructions] = useState(false)
    const [copied, setCopied] = useState(false)

    // CSRF token from <meta>
    useEffect(() => {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        setCsrfToken(token)
    }, [])

    // Detect if opened in webview
    useEffect(() => {
        const detectWebView = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera
            const standalone = window.navigator.standalone

            // Detect iOS
            const iosDevice = /(iPhone|iPod|iPad)/.test(userAgent)
            setIsIOS(iosDevice)

            // Check for Android WebView - most reliable is the 'wv' flag
            const isAndroidWebView = /; wv\)/.test(userAgent)

            // Check for iOS standalone mode (PWA) - not a webview
            const isIOSStandalone = standalone !== undefined && standalone

            // iOS check: if it's iOS, has AppleWebKit, but doesn't have Safari/CriOS/FxiOS/EdgiOS
            // and is not a PWA
            const isIOSInApp = iosDevice &&
                              /AppleWebKit/.test(userAgent) &&
                              !/(Safari|CriOS|FxiOS|EdgiOS)/.test(userAgent) &&
                              !isIOSStandalone

            // Popular app webviews (explicit app identifiers in user agent)
            const isFacebookApp = /FBAN|FBAV|FB_IAB/.test(userAgent)
            const isInstagramApp = /Instagram/.test(userAgent)
            const isLinkedInApp = /LinkedInApp/.test(userAgent)
            const isTwitterApp = /Twitter/.test(userAgent)

            // Check if it claims to be Chrome on Android but lacks window.chrome object
            // Real Chrome always has window.chrome, but webviews often don't
            const isAndroidChromeLike = /Android/.test(userAgent) &&
                                       /Chrome/.test(userAgent) &&
                                       !/SamsungBrowser|EdgA|OPR/.test(userAgent)
            const lacksRealChromeFeatures = isAndroidChromeLike && !window.chrome

            return isAndroidWebView || isIOSInApp || isFacebookApp || isInstagramApp ||
                   isLinkedInApp || isTwitterApp || lacksRealChromeFeatures
        }

        const isInWebView = detectWebView()
        setIsWebView(isInWebView)

        // Skip button and show instructions immediately for all webviews
        // Intent URLs are unreliable and often just refresh the page
        if (isInWebView) {
            setShowInstructions(true)
        }
    }, [])

    // Handle opening in external browser
    const handleOpenInBrowser = () => {
        const currentUrl = window.location.href
        // Get current protocol (http or https) and remove the colon
        const scheme = window.location.protocol.replace(':', '')

        // Try Android intent with Chrome package specified
        // Try Chrome first, with fallback to default browser
        const intentUrl = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(currentUrl)};end`

        let intentWorked = false

        // Listen for page visibility changes to detect if user left
        const handleVisibilityChange = () => {
            if (document.hidden || document.visibilityState === 'hidden') {
                intentWorked = true
            }
        }

        const handlePageHide = () => {
            intentWorked = true
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pagehide', handlePageHide)

        // Attempt to trigger the intent with direct navigation
        try {
            window.location.href = intentUrl
        } catch (e) {
            console.log('Intent failed:', e)
        }

        // Only show instructions if still visible after 1.5 seconds
        setTimeout(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('pagehide', handlePageHide)

            if (!intentWorked) {
                setShowInstructions(true)
            }
        }, 1500)
    }

    // Handle copy URL to clipboard
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = window.location.href
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            document.body.appendChild(textArea)
            textArea.select()
            try {
                document.execCommand('copy')
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            } catch (err) {
                console.error('Failed to copy:', err)
            }
            document.body.removeChild(textArea)
        }
    }

    // Validate password match
    useEffect(() => {
        if (!password || !confirmPassword) {
            setValidationError(null)
        } else if (password !== confirmPassword) {
            setValidationError('Passwords do not match')
        } else {
            setValidationError(null)
        }
    }, [password, confirmPassword])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        setStatus(null)

        if (validationError || !password || !confirmPassword) return

        setLoading(true)

        const resourceKey = type === 'client' ? 'client' : 'candidate'

        const body = {
            [resourceKey]: {
                reset_password_token: resetToken,
                password,
                password_confirmation: confirmPassword,
            },
        }

        const endpointMap = {
            candidate: '/api/v1/candidates/password',
            client: '/api/v1/clients/password',
        }

        const endpoint = endpointMap[type]

        if (!endpoint || !resetToken || !csrfToken) {
            setError('Missing reset token, login type, or CSRF token.')
            setLoading(false)
            return
        }

        try {
            const res = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify(body),
            })

            const contentType = res.headers.get('content-type') || ''
            let data = {}

            if (contentType.includes('application/json')) {
                data = await res.json()
            } else {
                console.warn('Non-JSON response:', await res.text())
            }

            console.debug('Response status:', res.status)

            if (res.ok || res.status === 303) {
                setStatus('Password successfully updated. Redirecting...')
                console.log('✅ Password reset successful, redirecting to app')

                setTimeout(() => {
                    window.location.href = 'https://app.ramsdenrecruitment.co.uk/'
                }, 2000)
            } else {
                const errMsg = data?.error || `Unexpected error: HTTP ${res.status}`
                console.error('❌ Password reset failed:', errMsg)
                setError(errMsg)
            }
        } catch (err) {
            console.error('❌ Network or parsing error:', err)
            setError('A network or server error occurred. Please try again.')
        } finally {
            setLoading(false)
        }
    }


    // If in webview, show browser redirect notice
    if (isWebView) {
        return (
            <div className="w-full space-y-6">
                {/* Header */}
                <div className="text-center space-y-2">
                    <div className="flex justify-center mb-3">
                        <div className="bg-[#8C3161]/10 p-3 rounded-full">
                            <AlertCircle className="h-6 w-6 text-[#8C3161]" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Open in Browser</h2>
                    <p className="text-sm text-gray-600">
                        For security reasons, please continue in your default browser.
                    </p>
                </div>

                {/* Notice Alert */}
                <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Browser Required</AlertTitle>
                    <AlertDescription className="text-amber-700">
                        Password setup must be completed in Chrome, Safari, or your default browser for security.
                    </AlertDescription>
                </Alert>

                {/* Instructions */}
                <div className="space-y-4">
                    <Alert className="border-blue-200 bg-blue-50">
                        <AlertDescription className="text-blue-900">
                            <p className="font-medium mb-3">Please follow these steps to continue:</p>
                            <div className="space-y-3 text-sm">
                                <div className="bg-white/50 p-3 rounded border border-blue-200">
                                    <p className="font-medium mb-1">Gmail App:</p>
                                    <ol className="list-decimal list-inside space-y-1 ml-2">
                                        <li>Tap the three dots (⋮) in the top right</li>
                                        <li>Select "Open in Browser"</li>
                                    </ol>
                                </div>
                                <div className="bg-white/50 p-3 rounded border border-blue-200">
                                    <p className="font-medium mb-1">Other Apps:</p>
                                    <ol className="list-decimal list-inside space-y-1 ml-2">
                                        <li>Look for menu (⋮ or ⋯) icon</li>
                                        <li>Select "Open in Browser" or "Open in Chrome/Safari"</li>
                                    </ol>
                                    <p className="mt-2 ml-2 font-medium">OR</p>
                                    <p className="ml-2">Copy the URL below and paste it into Chrome or Safari</p>
                                </div>
                            </div>
                        </AlertDescription>
                    </Alert>

                    {/* URL Display with Copy Button */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-2 font-medium">Page Address:</p>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-xs text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                                {window.location.href}
                            </div>
                            <Button
                                onClick={handleCopyUrl}
                                variant="outline"
                                className="shrink-0 border-[#8C3161] text-[#8C3161] hover:bg-[#8C3161]/10"
                                size="sm"
                            >
                                {copied ? (
                                    <>
                                        <Check className="h-4 w-4 mr-1" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-4 w-4 mr-1" />
                                        Copy
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
                <div className="flex justify-center mb-3">
                    <div className="bg-[#8C3161]/10 p-3 rounded-full">
                        <Lock className="h-6 w-6 text-[#8C3161]" />
                    </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Create Your Password</h2>
                <p className="text-sm text-gray-600">
                    Welcome to Ramsden Recruitment! Please set up your password to get started.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password Field */}
                <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                        New Password
                    </Label>
                    <div className="relative">
                        <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            className="pr-10"
                            disabled={loading || status}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            disabled={loading || status}
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                        Confirm Password
                    </Label>
                    <div className="relative">
                        <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter your password"
                            className="pr-10"
                            disabled={loading || status}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            disabled={loading || status}
                        >
                            {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {validationError && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <span>⚠</span> {validationError}
                        </p>
                    )}
                </div>

                {/* Submit Button */}
                <Button
                    type="submit"
                    className="w-full bg-[#8C3161] hover:bg-[#73274f] text-white font-medium py-2.5"
                    disabled={loading || validationError || !password || !confirmPassword || status}
                >
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="animate-spin">⏳</span>
                            Setting password...
                        </span>
                    ) : status ? (
                        <span className="flex items-center justify-center gap-2">
                            <span className="animate-spin">⏳</span>
                            Redirecting...
                        </span>
                    ) : (
                        'Create Password'
                    )}
                </Button>
            </form>

            {/* Success Message */}
            {status && (
                <Alert className="border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Success!</AlertTitle>
                    <AlertDescription className="text-green-700">{status}</AlertDescription>
                </Alert>
            )}

            {/* Error Message */}
            {error && (
                <Alert variant="destructive">
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
        </div>
    )
}
